import { CreateMetadataResponse } from '@vindr/services/apis/uploadAttachmentFile';
import { CONFIG_SERVER, MIME_TO_EXTENSION } from '@vindr/services/constants/common.constant';
import keycloak from '@vindr/services/keycloak';
import { ProgressProps } from 'antd';
import pLimit from 'p-limit';
import { Dispatch } from 'react';
import S3Uploader, { S3UploaderConfig } from '@vindr/services/S3Uploader';
import Uploader, { UploaderConfig } from '../Uploader';
import { Appearance, Status, UploaderAction, UploaderState, onUploadingProps } from './type';
import { getExtensionFileName } from '@vindr/utils/converter';
import { DEFAULT_URL_UPLOAD } from '@vindr/services/constants/common.constant';
import { isPublicPortal } from '@vindr/utils/helpers';

const fifteenMB = 15 * 1024 * 1024;
const limit = pLimit(6);

export const handleUpload = async ({
  device_code,
  dispatch,
  fileList,
  metadata,
  params,
  type,
}: {
  device_code?: string;
  metadata?: CreateMetadataResponse['data']['metadata'];
  fileList: UploaderState['fileList'];
  dispatch: Dispatch<UploaderAction>;
  type?: Appearance;
  params?: any;
}) => {
  try {
    dispatch({ type: 'isUploading', payload: true });
    dispatch({ type: 'isFinish', payload: false });

    if (fileList.length === 0) return false;
    let splitLimitSize = null;

    if (type === 'attachment') {
      // With attachment file:
      // Create presigned url for all file to upload to s3
      // Create metadata
      const requestOptions = {
        method: 'POST',
        body: JSON.stringify({
          file_names: fileList.map((it) => it.name),
          action: 'UPLOAD',
        }),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${keycloak.getPermissionToken()}`,
        },
      };

      const presignUrlData = await fetch(
        CONFIG_SERVER.BACKEND_URL + '/ris/attachment/v1/upload/url',
        requestOptions
      ).then((response) => response?.json?.());
      const presignUrls = presignUrlData?.data?.presigned_object;
      if (!presignUrls) return false;

      const metaOptions = {
        method: 'POST',
        body: JSON.stringify({
          ...params,
          study_description: Array.isArray(params?.study_description)
            ? params?.study_description?.join(',')
            : params?.study_description,
          file_infos: fileList.map((it) => ({
            file_name: it.name,
            original_file_name: it.originalName || it.name,
          })),
          file_names: fileList.map((it) => it.name),
          action: 'UPLOAD',
        }),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${keycloak.getPermissionToken()}`,
        },
      };
      const status = await fetch(
        CONFIG_SERVER.BACKEND_URL + '/ris/attachment/v1/upload-file-metadata',
        metaOptions
      ).then((response) => response.status);
      if (status !== 200) return false;

      splitLimitSize = fileList.map((it) => [
        {
          ...it,
          presignUrl: presignUrls.find((urlIt) => urlIt.file_name === it.name)?.url,
        },
      ]);
    } else {
      splitLimitSize = splitChunks(fileList) as Array<UploaderState['fileList']>;
    }

    Promise.all(
      splitLimitSize.map((chunkFile) =>
        limit(() =>
          onUploading({
            device_code,
            chunk: chunkFile,
            dispatch,
            metadata,
            type,
            params,
          })
        )
      )
    );
    return true;
  } catch (err) {
    console.log(err);
    return false;
  }
};

function onUploading({
  device_code,
  chunk,
  dispatch,
  metadata,
  type,
  params = {},
}: onUploadingProps) {
  let dataToSend = null;
  const uploaderConfig: S3UploaderConfig | UploaderConfig = {
    type,
  };
  let uploader = null;

  if (type === 'attachment') {
    if (isPublicPortal()) {
      // S3 upload need presignUrl
      const file = chunk[0];
      uploaderConfig.url = file.presignUrl;
      const mimeType = MIME_TO_EXTENSION[getExtensionFileName(file.name)];
      if (mimeType) {
        uploaderConfig.headers = {
          'Content-Type': mimeType,
        };
      }
      uploader = new S3Uploader(uploaderConfig);
      dataToSend = file.originFileObj;
    } else {
      uploaderConfig.method = 'POST';
      uploaderConfig.type = 'attachment';
      uploaderConfig.url = `${DEFAULT_URL_UPLOAD}/api/ris/attachment/v1/upload-files`;
      uploaderConfig.headers = {
        'Authorization': `Bearer ${keycloak.getPermissionToken()}`,
      };
      uploader = new Uploader(uploaderConfig);
      const formData = new FormData();

      // Object.entries(params).forEach(([key, value]) => {
      //   formData.append(key, value);
      // });
      formData.append('action', 'UPLOAD');

      chunk.forEach((file) => {
        formData.append('file', file.originFileObj, file.name);
      });
      dataToSend = formData;
    }
  } else {
    uploaderConfig.headers = {
      'Authorization': `Bearer ${keycloak.getPermissionToken()}`,
      'Accept': 'application/dicom+json',
      'Content-Type': `multipart/related;type="application/dicom${metadata ? '+json' : ''}"`,
      ...(device_code && { 'X-DEVICE-CODE': device_code }),
    };
    uploaderConfig.type = 'dicom';
    if (metadata) {
      uploaderConfig.metadata = metadata?.find((item) => item.filename === chunk[0].name)?.metadata;
    }
    uploader = new Uploader(uploaderConfig);

    const formData = new FormData();

    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value);
    });

    chunk.forEach((file) => {
      formData.append('file', file.originFileObj, file.name);
    });
    dataToSend = formData;
  }

  const chunksID = chunk.map((file) => file.fileId);

  dispatch({ type: 'setAbort', payload: uploader.abort.bind(uploader) });

  uploader.onProgress = ({ status, percent }) => {
    let stateStatus = 'uploading';

    if (typeof status === 'number') {
      stateStatus = status === 200 ? 'success' : 'exception';
    }

    dispatch({
      type: 'setProgress',
      payload: { ids: chunksID, progress: percent, status: stateStatus },
    });
  };

  uploader.end = () => {
    dispatch({
      type: 'setProgress',
      payload: { ids: chunksID, progress: 100, status: 'success' },
    });
    console.log('SUCCESS upload chunk ', chunksID);
    dispatch({ type: 'setRemainingCount', payload: chunk.length });
  };

  uploader.error = (error) => {
    if (!error) return;

    console.log('ERROR upload chunk ', chunksID);
    dispatch({ type: 'setProgress', payload: { ids: chunksID, progress: 0, status: 'exception' } });
    dispatch({ type: 'setRemainingCount', payload: chunk.length });
    dispatch({ type: 'failItem', payload: { ids: chunksID, error } });
  };

  return uploader.send(dataToSend);
}

function splitChunks(arr: UploaderState['fileList'], total = fifteenMB) {
  const result = [];
  let temp = [];
  let size = 0;

  arr.forEach((file) => {
    if (file.size > total) {
      result.push([file]);

      return;
    }

    if (size + file.size > total) {
      result.push(temp);
      temp = [];
      size = 0;
    }
    temp.push(file);
    size += file.size;
  });

  if (temp.length) {
    result.push(temp);
  }

  return result;
}

export function statusMap(status: Status): ProgressProps['status'] | 'converting' {
  switch (status) {
    case 'uploading':
      return 'active';
    case 'ready':
      return 'normal';
    case 'success':
      return 'success';
    case 'exception':
      return 'exception';
    case 'converting':
      return 'converting';
  }
}
